import planMaterialStockChange from './planMaterialStockChange'
import notDeleted from '../notDeleted'

// AI-CONTEXT: See docs/patterns.md#data-fresh-fetch-on-mutation

/**
 * Apply a single-material stock change for a household material entry.
 *
 * Fetches the Material fresh (tenant-filtered) right before the write — never
 * trusts a stale listener snapshot — then runs the pure planner and applies the
 * clamped read-modify-write. Last-write-wins accepted under v1 (see pattern).
 *
 * - `delta < 0` deducts (add / increase a household material)
 * - `delta > 0` restores (remove / decrease)
 *
 * Met `allowNegative` (instelling 'Voorraad mag negatief worden') wordt een
 * tekort niet geweigerd maar doorgeboekt; de voorraad komt dan onder 0 te
 * staan. De bevestiging hoort dan al gevraagd te zijn — zie
 * `collectNegativeStockWarnings`.
 *
 * @param {object} db - Firestore instance
 * @param {string} client - Tenant CompagnyID
 * @param {string} materialID - Materials.ID (not the docid)
 * @param {number} delta - Negative deducts, positive restores
 * @param {{allowNegative?: boolean}} [options]
 * @returns `{ blockedBy }` when the deduction is refused (insufficient stock);
 *          otherwise `{ ok: true }` (including the orphan/zero-delta no-ops).
 */
const runMaterialStockChange = async (db, client, materialID, delta, { allowNegative = false } = {}) => {
    const amount = Number(delta) || 0
    if (!materialID || amount === 0) return { ok: true }

    const snap = await db.collection('Materials')
        .where('CompagnyID', '==', client)
        .where('ID', '==', materialID)
        .limit(1)
        .get()

    // Material was deleted from the master list — nothing to adjust.
    if (snap.empty) return { ok: true }

    const doc = snap.docs[0]
    const material = { ...doc.data(), docid: doc.id }

    // Zacht verwijderd: de organisatie voert het niet meer, maar regels bij
    // bewoners wijzen er nog naar (inbox #284). Hetzelfde als een materiaal dat
    // hard weg was — geen voorraad om bij te houden, en dus ook geen tekort om
    // een aanpassing van die regel op te weigeren.
    if (!notDeleted(material)) return { ok: true }

    const { op, blockedBy } = planMaterialStockChange(material, amount, { allowNegative })
    if (blockedBy) return { blockedBy }
    if (!op) return { ok: true }

    // De clamp op 0 hoort bij de blokkade: zonder `allowNegative` kan een
    // doorgelaten mutatie nooit onder 0 uitkomen, en is de clamp alleen nog een
    // vangnet. Mét `allowNegative` moet het tekort juist zichtbaar blijven.
    const current = Number(material.QuantityInStock) || 0
    const raw = current + op.delta
    const next = allowNegative ? raw : Math.max(0, raw)
    await db.collection('Materials').doc(material.docid).update({ QuantityInStock: next })

    return { ok: true }
}

export default runMaterialStockChange
